/**
 * Token metadata loaded from src/tokens/*.json files.
 *
 * To add a new token, create a JSON file in src/tokens/ with:
 *   { "name": "TICKER", "decimals": 6, "icon": "/images/logo.png" }
 *
 * The filename doesn't matter — tokens are keyed by their "name" field.
 */

interface TokenMeta {
  name: string;
  decimals: number;
  icon?: string;
}

// Vite eager glob import — resolved at build time
const metaFiles = import.meta.glob<TokenMeta>("@/tokens/*.json", {
  eager: true,
  import: "default",
});

const registry = new Map<string, TokenMeta>();

for (const mod of Object.values(metaFiles)) {
  if (mod?.name) {
    registry.set(mod.name, mod);
  }
}

const DEFAULT_DECIMALS = 6;

/** Look up decimals for a token by its display name. Defaults to 6. */
export function getTokenDecimals(name: string): number {
  return registry.get(name)?.decimals ?? DEFAULT_DECIMALS;
}

/** Look up icon path for a token by its display name. */
export function getTokenIcon(name: string): string | undefined {
  return registry.get(name)?.icon;
}

/** Get full metadata for a token, if available. */
export function getTokenMeta(name: string): TokenMeta | undefined {
  return registry.get(name);
}
